import { Box, Flex, Tag, Text } from "@chakra-ui/react";
import React from "react";

import { EmptyAIIcon, RouterLink, SortableTable } from "../../../components";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { PageOptions } from "../../../hooks/useOffsetPagination";
import { formatRelativeDate } from "../../../utils/datetime";
import {
  AiInterviewerListItemFragment,
  AiInterviewerStatus,
} from "../../graphql";
import {
  REQUIRES_BH_SCREEN_MANAGE_PERMISSION,
  useBHScreenPermission,
} from "../../hooks/useBHScreenPermission";
import { CreateAiInterviewerButton } from "../../pages/ai-interviewers/CreateAiInterviewerButton";
import { AiInterviewerActions } from "./AiInterviewerActions";

const AiInterviewersList: React.FC<{
  aiInterviewers: AiInterviewerListItemFragment[];
  hasFiltersApplied: boolean;
  pageOptions: PageOptions;
}> = ({ aiInterviewers, hasFiltersApplied, pageOptions }) => {
  const { canManage } = useBHScreenPermission();
  const isSmallScreen = useIsSmallScreen();

  return (
    <Flex w="100%" flexDirection="column">
      <SortableTable<AiInterviewerListItemFragment>
        columns={[
          {
            Header: isSmallScreen ? "Name" : "AI Interviewer Name",
            accessor: "name",
            Cell: ({
              row: { original: interviewer },
            }: {
              row: { original: AiInterviewerListItemFragment };
            }) => (
              <RouterLink to={`/ai-interviewer/${interviewer.id}`}>
                {interviewer.name || "Untitled"}
              </RouterLink>
            ),
          },
          {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }: { value?: AiInterviewerStatus | null }) =>
              value ? <StatusTag status={value} /> : null,
          },
          {
            Header: "Interviews",
            accessor: "interviewsCount",
          },
          // TODO: clarify and implement "Linked to position + stage" column
          {
            Header: "Author",
            id: "currentVersion.createdBy.fullName",
            accessor: (row) =>
              row.currentVersion.createdBy.fullName || "Unknown author",
            show: !isSmallScreen,
          },
          {
            Header: "Created date",
            accessor: "createdAt",
            show: !isSmallScreen,
            Cell: ({ value }: { value: string }) => (
              <Box whiteSpace="nowrap">{formatRelativeDate(value)}</Box>
            ),
          },
          {
            Header: " ", // Empty header for the menu (must be a space)
            show: !isSmallScreen,
            Cell: ({
              row: { original: interviewer },
            }: {
              row: { original: AiInterviewerListItemFragment };
            }) => <AiInterviewerActions interviewer={interviewer} />,
          },
        ]}
        initialSort={pageOptions.sortBy}
        data={aiInterviewers}
        width="100%"
        manualPagination
        pageOptions={pageOptions}
      />
      {aiInterviewers.length === 0 && !hasFiltersApplied && (
        <Box w="100%" textAlign="center" py="6">
          <EmptyAIIcon />
          <Text mt="4" mb="6" fontWeight="600" fontSize="lg">
            No AI interviewers to display yet. Build one today!
          </Text>
          {!isSmallScreen && (
            <CreateAiInterviewerButton
              disabledReason={
                !canManage ? REQUIRES_BH_SCREEN_MANAGE_PERMISSION : undefined
              }
            />
          )}
        </Box>
      )}
      {aiInterviewers.length === 0 && hasFiltersApplied && (
        <Box w="100%" textAlign="center" py="6">
          <EmptyAIIcon />
          <Text mt="4" mb="6" fontWeight="600" fontSize="lg">
            No interviewers match your search.
          </Text>
        </Box>
      )}
    </Flex>
  );
};

const StatusTag: React.FC<{ status: AiInterviewerStatus }> = ({ status }) => {
  return (
    <Tag
      size="sm"
      colorScheme={status === AiInterviewerStatus.Live ? "green" : "gray"}
    >
      {status}
    </Tag>
  );
};

export default AiInterviewersList;
